"use client";

import { useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";

import { LazyMotion, MotionDiv } from "@/components/animate";
import { cn } from "@/lib/utils";

import { CareersDeptChip } from "./careers-dept-chip";
import { NavPreviewCareersGradientBackground } from "./nav-preview-careers-gradient";
import { navPreviewItem, navPreviewMotionProps } from "./nav-preview-motion";
import { NavFloatingCard, NavPreviewShell } from "./nav-preview-shell";

/** Figma Tag / type Warning (7253:48649–48651): 12px strong label, 4px inset, 4px radius. */
const CAREERS_PREVIEW_TAG_BASE =
  "text-grey-950 text-ds-label-md inline-flex h-5 w-fit items-center rounded-ds-s px-1 py-1 font-medium leading-none";

/** Matches Figma nav dropdown careers preview (node 7253:48565). */
export function CompanyCareersNavPreview() {
  const t = useTranslations("Nav.preview.careers");
  const reduceMotion = useReducedMotion();
  const motionProps = navPreviewMotionProps(reduceMotion);

  return (
    <NavPreviewShell>
      <NavPreviewCareersGradientBackground />

      <NavFloatingCard>
        <LazyMotion>
          <MotionDiv className="relative h-full w-full" {...motionProps}>
            <MotionDiv
              className="absolute top-[25px] left-[29px] flex flex-col gap-2"
              variants={reduceMotion ? undefined : navPreviewItem}
            >
              <div className="flex items-center gap-2">
                <p className="text-ds-body-lg text-grey-950 leading-[1.3] tracking-[-0.36px]">
                  {t("openPositions")}
                </p>
                <CareersPreviewTag
                  label={t("openingsCount")}
                  className="bg-green-100"
                />
              </div>
              <div className="flex items-center gap-2">
                <CareersPreviewTag
                  label={t("filters.fullTime")}
                  className="bg-pulse-100"
                />
                <CareersPreviewTag
                  label={t("filters.remote")}
                  className="bg-salmon-100"
                />
              </div>
            </MotionDiv>

            <MotionDiv
              className="absolute top-[102px] left-[27px] flex gap-[14px] opacity-90"
              variants={reduceMotion ? undefined : navPreviewItem}
            >
              <CareersDeptChip
                label={t("departments.commercial")}
                tone="commercial"
              />
              <CareersDeptChip
                label={t("departments.humanResources")}
                tone="hr"
              />
              <CareersDeptChip
                label={t("departments.sales")}
                tone="sales"
                emphasis="muted"
              />
            </MotionDiv>

            <MotionDiv
              className="absolute top-[144px] left-[27px] flex gap-[14px] opacity-90"
              variants={reduceMotion ? undefined : navPreviewItem}
            >
              <CareersDeptChip
                label={t("departments.data")}
                tone="data"
                emphasis="faint"
              />
              <CareersDeptChip
                label={t("departments.engineering")}
                tone="engineering"
                emphasis="faint"
              />
              <CareersDeptChip
                label={t("departments.marketing")}
                tone="marketing"
                emphasis="faint"
              />
            </MotionDiv>

            <MotionDiv
              className="absolute top-[186px] left-[27px] flex flex-wrap gap-[14px] opacity-90"
              variants={reduceMotion ? undefined : navPreviewItem}
            >
              <CareersDeptChip
                label={t("departments.sales")}
                tone="sales"
                emphasis="muted"
              />
              <CareersDeptChip
                label={t("departments.design")}
                tone="design"
                emphasis="muted"
              />
              <CareersDeptChip
                label={t("departments.operations")}
                tone="operations"
                emphasis="muted"
              />
              <CareersDeptChip
                label={t("departments.commercial")}
                tone="commercial"
              />
            </MotionDiv>
          </MotionDiv>
        </LazyMotion>
      </NavFloatingCard>
    </NavPreviewShell>
  );
}

function CareersPreviewTag({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  return (
    <span className={cn(CAREERS_PREVIEW_TAG_BASE, className)}>{label}</span>
  );
}
